'use client';

import * as React from 'react';
import * as Sentry from '@sentry/nextjs';

import { useMutation } from '@apollo/client/react';
import { SubscribeEmailToIosWaitlistDocument } from '@gql';
import { useAnalytics } from '@contexts/use-analytics';

import { Button } from '@components/button';
import { Spinner } from '@components/spinner';
import { Typography } from '@components/typography';
import { Icon, IconNames } from '@components/icon';

export const IOS_WAITLIST_LEAD_SOURCE = 'ios_waitlist';

type Variant = 'hero' | 'inline';

type Props = {
  variant?: Variant;
  /** Optional override for the call-to-action label above the field. */
  label?: string;
  /** Optional override for the success headline shown after submit. */
  successTitle?: string;
  /** Optional override for the success body shown after submit. */
  successBody?: string;
};

/**
 * Captures emails for the iOS app waitlist (BHT-5487). Submissions land in a
 * dedicated Klaviyo list via the `subscribeEmailToIosWaitlist` mutation, and
 * fire a `generate_lead` analytics event with `source: ios_waitlist` so we can
 * gauge interest in GTM/GA4.
 */
export const IosWaitlistForm: React.FC<Props> = ({
  variant = 'inline',
  label = 'Want the iOS app? Join the waitlist:',
  successTitle = "You're on the list!",
  successBody = "We'll email you the moment the iOS app is ready.",
}) => {
  const [email, setEmail] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubscribed, setIsSubscribed] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const [subscribeEmailToIosWaitlist] = useMutation(
    SubscribeEmailToIosWaitlistDocument
  );
  const { emitGenerateLeadEvent } = useAnalytics();

  const onChangeHandler = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(value);
  };

  const onSubmitHandler = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      setSubmitError(null);
      const result = await subscribeEmailToIosWaitlist({ variables: { email } });
      const payload = result.data?.subscribeEmailToIosWaitlist;

      if (payload?.__typename === 'IosWaitlistSubscription') {
        emitGenerateLeadEvent({
          source: IOS_WAITLIST_LEAD_SOURCE,
          account_name: '',
        });
        setEmail('');
        setIsSubscribed(true);
      } else if (payload?.__typename === 'ValidationErrorCollection') {
        const firstError = payload.errors[0];
        setSubmitError(
          firstError?.message ?? 'Please enter a valid email address.'
        );
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('ios_waitlist_subscribe_failed', { error });
      Sentry.captureException(error);
      setSubmitError('Subscription failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isHero = variant === 'hero';

  if (isSubscribed) {
    return (
      <div
        className={
          isHero
            ? 'flex flex-col gap-2'
            : 'flex flex-col gap-2 text-secondary-light'
        }
        role="status"
      >
        <Typography
          variant={isHero ? 'subtitle1' : 'subtitle2'}
          className={isHero ? 'text-primary-main' : 'text-secondary-light'}
        >
          {successTitle}
        </Typography>
        <Typography
          variant="body2"
          className={isHero ? 'text-primary-main' : 'text-secondary-light'}
        >
          {successBody}
        </Typography>
      </div>
    );
  }

  if (isHero) {
    return (
      <div className="flex w-full max-w-xl flex-col gap-3">
        <Typography
          variant="subtitle1"
          className="text-left text-primary-main"
        >
          {label}
        </Typography>
        <form
          onSubmit={onSubmitHandler}
          className="flex flex-row items-center border border-primary-main rounded-full bg-white overflow-hidden w-full"
        >
          <label className="sr-only" htmlFor="ios-waitlist-email-hero">
            Email address
          </label>
          <input
            id="ios-waitlist-email-hero"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={onChangeHandler}
            placeholder="name@email.com"
            className="px-5 py-3 bg-transparent text-primary-main placeholder-gray-main outline-none w-full"
            required
          />
          <Button
            type="submit"
            variant="filled"
            size="md"
            className="rounded-full m-1 px-6 whitespace-nowrap"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            dataId="ios-waitlist-form-submit"
          >
            Join waitlist
          </Button>
        </form>
        {submitError && (
          <Typography
            variant="small"
            component="p"
            className="text-error-main mt-1"
          >
            {submitError}
          </Typography>
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-center justify-end gap-4 lg:space-x-2 lg:flex-row pt-4 lg:pt-0">
      <div className="flex flex-col text-left items-start w-full lg:w-auto whitespace-nowrap">
        <Typography variant="body1" className="text-left text-secondary-main">
          {label}
        </Typography>
      </div>
      <div className="flex flex-col w-full">
        <form
          onSubmit={onSubmitHandler}
          className="flex flex-row items-center border border-white rounded-full overflow-hidden w-full max-w-md"
        >
          <label className="sr-only" htmlFor="ios-waitlist-email-inline">
            Email address
          </label>
          <input
            id="ios-waitlist-email-inline"
            name="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={onChangeHandler}
            placeholder="name@email.com"
            className="px-4 py-2 bg-transparent text-white placeholder-white outline-none w-full"
            required
          />
          <Button
            type="submit"
            variant="text"
            size="md"
            className="pr-4 py-2 text-white hover:text-secondary-main transition-colors w-20 items-center justify-center"
            disabled={isSubmitting}
            dataId="ios-waitlist-form-submit"
          >
            {isSubmitting ? (
              <Spinner className="text-white" />
            ) : (
              <Icon
                name={IconNames.PAPER_PLANE}
                size="lg"
                className="text-white"
              />
            )}
          </Button>
        </form>
        {submitError && (
          <Typography
            variant="small"
            component="p"
            className="text-error-light mt-2"
          >
            {submitError}
          </Typography>
        )}
      </div>
    </div>
  );
};
